import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { CoverCard } from '../components/CoverCard';
import { Panel } from '../components/ui/panel';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { useCurrentUser } from '../features/auth/session';
import { useStats } from '../features/collection/queries';
import { imageUrl } from '../lib/api';
import { coverGradient } from '../lib/cover';
import { seriesCoverTransition } from '../lib/transitions';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const stats = useStats();

  const tiles = [
    { label: t('dashboard.issues'), value: stats.data ? String(stats.data.copyCount) : '–' },
    { label: t('dashboard.series'), value: stats.data ? String(stats.data.seriesCount) : '–' },
    { label: t('dashboard.wishlist'), value: stats.data ? String(stats.data.wishlistCount) : '–' },
    {
      label: t('dashboard.value'),
      value: stats.data ? formatCurrency(stats.data.purchaseValue) : '–',
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-3xl text-foreground">{t('dashboard.title')}</h1>
        {user ? (
          <p className="mt-1 text-sm text-muted">
            {t('dashboard.welcome', { username: user.username })}
          </p>
        ) : null}
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((tile) => (
          <Panel key={tile.label} className="p-4">
            <p className="text-xs tracking-wide text-muted uppercase">{tile.label}</p>
            <p className="mt-2 font-display text-3xl text-foreground">{tile.value}</p>
          </Panel>
        ))}
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-xl text-foreground">{t('dashboard.almostComplete')}</h2>

        {stats.isPending ? (
          <Skeleton className="h-28" />
        ) : (stats.data?.almostComplete.length ?? 0) === 0 ? (
          <Panel className="halftone p-6 text-center text-sm text-muted">
            {t('dashboard.almostCompleteEmpty')}
          </Panel>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {stats.data?.almostComplete.map((series) => (
              <Link key={series.id} to={`/series/${series.id}`} viewTransition>
                <Panel className="flex gap-4 p-4 transition-colors hover:border-accent">
                  <span
                    className="aspect-[2/3] w-12 shrink-0 overflow-hidden rounded border bg-surface-raised"
                    style={{
                      ...seriesCoverTransition(series.id),
                      ...(series.displayCoverImageId
                        ? {}
                        : { backgroundImage: coverGradient(series.title) }),
                    }}
                  >
                    {series.displayCoverImageId ? (
                      <img
                        src={imageUrl(series.displayCoverImageId, 'thumb')}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-foreground">{series.title}</span>
                    <Progress
                      className="mt-3"
                      value={series.ownedCount}
                      max={series.plannedCount}
                      label={t('series.progress', {
                        owned: series.ownedCount,
                        total: series.plannedCount,
                      })}
                    />
                  </span>
                </Panel>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-xl text-foreground">{t('dashboard.recent')}</h2>

        {stats.isPending ? (
          <Skeleton className="h-48" />
        ) : (stats.data?.recentlyAdded.length ?? 0) === 0 ? (
          <Panel className="halftone p-6 text-center text-sm text-muted">
            {t('dashboard.recentEmpty')}
          </Panel>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {stats.data?.recentlyAdded.map((row) => (
              <CoverCard
                key={row.item.id}
                title={row.seriesTitle}
                number={row.issue.number}
                subtitle={row.publisherName ?? undefined}
                imageUrl={imageUrl(row.coverImageId, 'thumb')}
                quantity={row.item.quantity}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
